/**
 * セクションのステータスピル（Ready / Review / Draft）。
 * クリックで選択肢を出し、選んだ時点で landing.sections[] に保存する。
 * 手で選んだステータスは statusManual=true にし、1 セクション再生成でも上書きされない。
 * ページ構成（PageOutline）と編集エリア（SectionEditorSheet）の両方で使う。
 */
import type { LandingSectionRef, LandingSectionStatus } from '@goenhub/shared';
import { Check, ChevronDown, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { toast } from '@/components/ui/toast';
import { useUpdateLanding } from '@/hooks/useLandings';

const OPTIONS: { value: LandingSectionStatus; label: string }[] = [
  { value: 'ready', label: 'Ready' },
  { value: 'needs_review', label: 'Review' },
  { value: 'draft', label: 'Draft' },
];

const PILL_CLASS: Record<LandingSectionStatus, string> = {
  ready: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  regenerating: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  needs_review: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

/** landing.sections[] の 1 件だけを差し替えて保存する（名前・ステータス変更で共用）。 */
export function useUpdateSectionRef(landingId: string, sections: LandingSectionRef[]) {
  const updateLanding = useUpdateLanding();
  return async (sectionId: string, patch: Partial<LandingSectionRef>) => {
    const next = sections.map((s) => (s.id === sectionId ? { ...s, ...patch } : s));
    await updateLanding.mutateAsync({ id: landingId, data: { sections: next } });
  };
}

interface Props {
  landingId: string;
  sections: LandingSectionRef[];
  section: LandingSectionRef;
  size?: 'sm' | 'md';
}

export function SectionStatusPill({ landingId, sections, section, size = 'sm' }: Props) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const updateRef = useUpdateSectionRef(landingId, sections);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = async (value: LandingSectionStatus) => {
    setOpen(false);
    if (value === section.status && section.statusManual) return;
    setSaving(true);
    try {
      await updateRef(section.id, { status: value, statusManual: true });
    } catch (e) {
      toast.error(`ステータスの変更に失敗しました: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const text = size === 'md' ? 'text-[11px] px-2 py-0.5' : 'text-[10px] px-1.5 py-0.5';
  const current = OPTIONS.find((o) => o.value === section.status);

  return (
    <div
      ref={rootRef}
      className="relative flex-shrink-0"
      // カード自体のクリック（セクション選択）やドラッグを起こさない
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        title="クリックでステータスを変更"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`inline-flex items-center gap-0.5 rounded-full whitespace-nowrap hover:brightness-95 disabled:opacity-60 ${text} ${
          PILL_CLASS[section.status] ?? PILL_CLASS.draft
        }`}
      >
        {section.status === 'regenerating' ? (
          <>
            <Sparkles className="size-2.5 inline animate-pulse" /> Regen…
          </>
        ) : (
          <>● {current?.label ?? section.status}</>
        )}
        <ChevronDown className="size-2.5 opacity-70" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-50 min-w-[112px] rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] shadow-lg py-1"
        >
          {OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="menuitem"
              onClick={() => void choose(o.value)}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-left hover:bg-[var(--bg-hover)]"
            >
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${PILL_CLASS[o.value]}`}>
                ● {o.label}
              </span>
              {o.value === section.status && (
                <Check className="size-3 ml-auto text-[var(--text-muted)]" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
